import { Mail, Lock, ArrowRight, Plus, ShieldCheck, MonitorCheck, CheckCircle2 } from 'lucide-react'
import AuthBrandHeader from '../../components/AuthBrandHeader'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { authApi } from '../../services/authApi'
import { tenantsApi } from '../../services/tenantsApi'
import Modal from '../../components/Modal/Modal'

function safeSessionGet(key) {
  if (typeof window === 'undefined') return ''
  try {
    return window.sessionStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

function safeSessionSet(key, value) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Ignora bloqueios de storage no browser
  }
}

function safeSessionRemove(key) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Ignora bloqueios de storage no browser
  }
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        d="M21.35 11.1H12v2.98h5.36c-.23 1.51-1.08 2.79-2.3 3.65v3.03h3.72c2.18-2.01 3.44-4.96 3.44-8.39 0-.42-.04-.84-.11-1.27z"
        fill="#4285F4"
      />
      <path
        d="M12 22c3.1 0 5.7-1.03 7.6-2.79l-3.72-3.03c-1.03.69-2.35 1.1-3.88 1.1-2.98 0-5.5-2.01-6.4-4.71H1.76v3.1A9.996 9.996 0 0 0 12 22z"
        fill="#34A853"
      />
      <path
        d="M5.6 12.57A5.99 5.99 0 0 1 5.24 10c0-.89.15-1.75.42-2.57v-3.1H1.76A9.996 9.996 0 0 0 0 10c0 1.61.39 3.14 1.08 4.5l4.52-1.93z"
        fill="#FBBC05"
      />
      <path
        d="M12 3.98c1.68 0 3.2.58 4.39 1.73l3.29-3.29C17.69.89 15.1 0 12 0 7.91 0 4.36 2.34 2.6 5.75l4.6 1.68c.9-2.7 3.42-4.71 6.4-4.71z"
        fill="#EA4335"
      />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, completeSso, completeLoginMfa, token, user, isAuthenticated } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenantOptions, setTenantOptions] = useState([])
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [ssoProvider, setSsoProvider] = useState('')
  const [ssoCode, setSsoCode] = useState('')
  const [ssoState, setSsoState] = useState('')
  const [ssoPendingToken, setSsoPendingToken] = useState('')
  const [mfaTicket, setMfaTicket] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [canCreateTenant, setCanCreateTenant] = useState(false)
  const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false)
  const [newTenantName, setNewTenantName] = useState('')
  const [isCreatingTenant, setIsCreatingTenant] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Fluxo de autenticação do agente Windows Connect.IO (login WEB):
  // o agente abre {portal}/login?source=windows&port={porta} e escuta em
  // http://localhost:{porta}/auth?token=...&userId=...&userName=...&tenantId=...
  const [windowsPort, setWindowsPort] = useState(null)
  const [windowsCallbackDone, setWindowsCallbackDone] = useState(false)
  const windowsCallbackAttemptedRef = useRef(false)

  const autoSsoAttemptedRef = useRef(false)
  const isSsoFlowActive = Boolean(ssoProvider || ssoPendingToken)
  const isMfaFlowActive = Boolean(mfaTicket)

  // ?device=Android — contexto de registro de dispositivo Android
  const isAndroidDevice = new URLSearchParams(window.location.search).get('device') === 'Android'

  // Dispara o callback de loopback para o agente Connect.IO com o token recém-obtido.
  const performWindowsCallback = async (authToken, authUser) => {
    const port = windowsPort || safeSessionGet('windowsAuthPort')
    if (!port || windowsCallbackAttemptedRef.current) return false
    windowsCallbackAttemptedRef.current = true

    try {
      const userId = authUser?.id || authUser?.Id || ''
      const userName = authUser?.name || authUser?.Name || authUser?.email || authUser?.Email || ''
      const tenantId = authUser?.tenantId || authUser?.TenantId || ''
      const qs = new URLSearchParams({ token: authToken, userId, userName, tenantId }).toString()
      await fetch(`http://localhost:${port}/auth?${qs}`, { mode: 'no-cors' })
      safeSessionRemove('windowsAuthPort')
      setWindowsCallbackDone(true)
      return true
    } catch {
      setError(t('auth.login.windowsCallbackFailed'))
      windowsCallbackAttemptedRef.current = false
      return false
    }
  }

  // Detectar autenticação do agente Windows: ?source=windows&port={porta} (persistido p/ sobreviver ao redirect SSO).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const source = params.get('source')
    const port = params.get('port')
    if (source === 'windows' && port) {
      setWindowsPort(port)
      safeSessionSet('windowsAuthPort', port)
    } else {
      const storedPort = safeSessionGet('windowsAuthPort')
      if (storedPort) setWindowsPort(storedPort)
    }
  }, [])

  // Se o navegador JÁ está autenticado e veio do agente Windows, dispara o callback imediatamente.
  useEffect(() => {
    if (isAuthenticated && windowsPort && token && user && !windowsCallbackAttemptedRef.current) {
      performWindowsCallback(token, user)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, windowsPort, token, user])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code') || ''
    const state = params.get('state') || ''
    const provider = safeSessionGet('ssoProviderPending')

    if (code && state && provider) {
      setSsoCode(code)
      setSsoState(state)
      setSsoProvider(provider)
      safeSessionRemove('ssoProviderPending')
      window.history.replaceState({}, '', '/login')
    }
  }, [])

  useEffect(() => {
    if (!ssoProvider || !ssoCode || !ssoState) return
    if (autoSsoAttemptedRef.current) return

    autoSsoAttemptedRef.current = true
    let cancelled = false

    const runAutoSso = async () => {
      setError('')
      setIsLoading(true)
      try {
        const result = await completeSso(ssoProvider, {
          code: ssoCode,
          state: ssoState,
        })
        if (cancelled) return

        if (result.requiresTenantSelection) {
          const options = result.tenants || []
          setTenantOptions(options)
          setSelectedTenantId((current) => current || options[0]?.tenantId || options[0]?.TenantId || '')
          setCanCreateTenant(Boolean(result.isGlobalUser || result.IsGlobalUser))
          if (result.ssoPendingToken) {
            setSsoPendingToken(result.ssoPendingToken)
            setSsoCode('')
            setSsoState('')
          }
          return
        }

        if (result.requiresMfa) {
          setMfaTicket(result.mfaTicket || '')
          setMfaCode('')
          return
        }

        if (result.success) {
          if (windowsPort && result.token && result.user) {
            await performWindowsCallback(result.token, result.user)
          } else {
            navigate(result.mustChangePassword ? '/definir-senha' : '/')
          }
        } else {
          setError(result.error || t('auth.login.errors.ssoCompletionFailed'))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('auth.login.errors.ssoAuthFailed'))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    runAutoSso()
    return () => {
      cancelled = true
    }
  }, [ssoProvider, ssoCode, ssoState, completeSso, navigate, t])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      let result
      if (isMfaFlowActive) {
        result = await completeLoginMfa(mfaTicket, mfaCode)
      } else if (ssoProvider && (ssoCode || ssoPendingToken)) {
        result = await completeSso(ssoProvider, {
          code: ssoCode || undefined,
          state: ssoState || undefined,
          pendingToken: ssoPendingToken || undefined,
          tenantId: selectedTenantId || undefined,
        })
      } else {
        result = await login(email, password, selectedTenantId || undefined)
      }

      if (result.requiresTenantSelection) {
        const options = result.tenants || []
        setTenantOptions(options)
        setSelectedTenantId((current) => current || options[0]?.tenantId || options[0]?.TenantId || '')
        setCanCreateTenant(Boolean(result.isGlobalUser || result.IsGlobalUser))
        if (result.ssoPendingToken) {
          setSsoPendingToken(result.ssoPendingToken)
          setSsoCode('')
          setSsoState('')
        }
        return
      }

      if (result.requiresMfa) {
        setMfaTicket(result.mfaTicket || '')
        setMfaCode('')
        return
      }

      if (result.success) {
        setCanCreateTenant(false)
        if (windowsPort && result.token && result.user) {
          await performWindowsCallback(result.token, result.user)
        } else {
          navigate(result.mustChangePassword ? '/definir-senha' : '/')
        }
      } else {
        setError(result.error || t('auth.login.errors.loginFailed'))
      }
    } catch (err) {
      setError(err.message || t('auth.login.errors.loginFailedRetry'))
    } finally {
      setIsLoading(false)
    }
  }

  const finalizeLoginWithTenant = async (tenantId) => {
    if (!tenantId) {
      throw new Error(t('auth.login.errors.invalidTenant'))
    }

    if (ssoProvider && (ssoCode || ssoPendingToken)) {
      return completeSso(ssoProvider, {
        code: ssoCode || undefined,
        state: ssoState || undefined,
        pendingToken: ssoPendingToken || undefined,
        tenantId,
      })
    }

    return login(email, password, tenantId)
  }

  const handleConfirmCreateTenant = async () => {
    const name = newTenantName.trim()
    if (!name) {
      setError(t('auth.login.createTenantModal.nameRequired'))
      return
    }

    setError('')
    setIsCreatingTenant(true)
    try {
      const createdTenant = await tenantsApi.create(name)
      const createdTenantId = createdTenant?.id || createdTenant?.Id
      if (!createdTenantId) {
        throw new Error(t('auth.login.createTenantModal.idError'))
      }

      const createdTenantName = createdTenant?.name || createdTenant?.Name || name
      setTenantOptions((current) => [
        ...current,
        { tenantId: createdTenantId, tenantName: createdTenantName },
      ])
      setSelectedTenantId(createdTenantId)

      const loginResult = await finalizeLoginWithTenant(createdTenantId)
      if (!loginResult?.success) {
        throw new Error(loginResult?.error || t('auth.login.createTenantModal.postCreateLoginFailed'))
      }

      setIsCreateTenantModalOpen(false)
      setNewTenantName('')
      setCanCreateTenant(false)

      if (windowsPort && loginResult.token && loginResult.user) {
        await performWindowsCallback(loginResult.token, loginResult.user)
      } else {
        navigate(loginResult.mustChangePassword ? '/definir-senha' : '/')
      }
    } catch (err) {
      setError(err.message || t('auth.login.errors.createTenant'))
    } finally {
      setIsCreatingTenant(false)
    }
  }

  const startSsoLogin = async (provider) => {
    setError('')
    setIsLoading(true)
    try {
      // redirectUri sem params extras para garantir compatibilidade com todos os provedores SSO.
      const redirectUri = `${window.location.origin}/login`
      safeSessionSet('ssoProviderPending', provider)
      const data = await authApi.startSso(provider, redirectUri)
      const url = data.authorizationUrl || data.AuthorizationUrl
      if (!url) throw new Error(t('auth.login.errors.ssoInvalidUrl'))
      window.location.assign(url)
    } catch (err) {
      safeSessionRemove('ssoProviderPending')
      setError(err.message || t('auth.login.errors.ssoStartFailed'))
      setIsLoading(false)
    }
  }

  // Tela de sucesso pós-callback do agente Windows Connect.IO
  if (windowsCallbackDone) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950 px-4 py-10">
        <div className="w-full max-w-md space-y-8">
          <AuthBrandHeader />
          <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gray-800/60 px-6 py-5 border-b border-gray-700/50 flex items-center gap-3">
              <div className="p-2 bg-green-600/20 rounded-lg">
                <MonitorCheck className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h1 className="text-white font-semibold text-base leading-tight">{t('auth.login.windowsSuccess.title')}</h1>
                <p className="text-gray-400 text-xs mt-0.5">{t('auth.login.windowsSuccess.subtitle')}</p>
              </div>
            </div>
            <div className="px-6 py-8 flex flex-col items-center gap-4 text-center">
              <div className="p-3 bg-green-600/20 rounded-full">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <div>
                <p className="text-white font-semibold text-lg">{t('auth.login.windowsSuccess.success')}</p>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                  <Trans i18nKey="auth.login.windowsSuccess.body" components={{ br: <br /> }} />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md mx-auto">
        <AuthBrandHeader />
      </div>

      <div className="mt-8 w-full max-w-md mx-auto">
        <div className="card py-8 px-4 shadow-lg sm:rounded-2xl sm:px-10">
          {/* Banner Android */}
          {isAndroidDevice && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
              <img src="/android.png" alt="" className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{t('auth.login.androidBanner')}</span>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Mensagem de erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!isSsoFlowActive && !isMfaFlowActive && (
              <>
                {/* Email */}
                <div>
                  <label htmlFor="email" className="label">
                    {t('auth.login.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                        setTenantOptions([])
                        setSelectedTenantId('')
                        autoSsoAttemptedRef.current = false
                        setSsoProvider('')
                        setSsoCode('')
                        setSsoState('')
                        setSsoPendingToken('')
                        setMfaTicket('')
                        setMfaCode('')
                      }}
                      className="input pl-10"
                      placeholder={t('auth.login.emailPlaceholder')}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="label">
                    {t('auth.login.passwordLabel')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setError('')
                        setTenantOptions([])
                        setSelectedTenantId('')
                        autoSsoAttemptedRef.current = false
                        setSsoProvider('')
                        setSsoCode('')
                        setSsoState('')
                        setSsoPendingToken('')
                        setMfaTicket('')
                        setMfaCode('')
                      }}
                      className="input pl-10"
                      placeholder={t('auth.login.passwordPlaceholder')}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </>
            )}

            {isMfaFlowActive && (
              <div>
                <label htmlFor="mfa-code" className="label">
                  {t('auth.login.mfaLabel')}
                </label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="mfa-code"
                    name="mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    required
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input pl-10"
                    placeholder={t('auth.login.mfaPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {t('auth.login.mfaHint')}
                </p>
              </div>
            )}

            {/* Seleção de tenant (apenas quando usuário tem múltiplos acessos) */}
            {tenantOptions.length > 0 && (
              <div>
                <label htmlFor="tenant" className="label">
                  {t('auth.login.tenantLabel')}
                </label>
                <select
                  id="tenant"
                  name="tenant"
                  value={selectedTenantId}
                  onChange={(e) => {
                    setSelectedTenantId(e.target.value)
                    setError('')
                  }}
                  className="input"
                  disabled={isLoading}
                  required
                >
                  {tenantOptions.map((tenant) => {
                    const tenantId = tenant.tenantId || tenant.TenantId
                    const tenantName = tenant.tenantName || tenant.TenantName || tenantId
                    return (
                      <option key={tenantId} value={tenantId}>
                        {tenantName}
                      </option>
                    )
                  })}
                </select>

              </div>
            )}

            {/* Remember me & Forgot password */}
            {!isSsoFlowActive && !isMfaFlowActive && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-theme-primary focus:ring-theme-primary"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    {t('auth.login.rememberMe')}
                  </label>
                </div>

                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="font-medium text-theme-primary hover:text-theme-primary/80"
                  >
                    {t('auth.login.forgotPasswordLink')}
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary py-3 text-base"
            >
              {isLoading ? (
                <span>{t('auth.login.submitLoading')}</span>
              ) : (
                <>
                  {isMfaFlowActive
                    ? t('auth.login.submitMfa')
                    : tenantOptions.length > 0
                      ? t('auth.login.submitTenant')
                      : ssoProvider
                        ? t('auth.login.submitSso')
                        : t('auth.login.submit')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {!tenantOptions.length && !ssoProvider && !isMfaFlowActive && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('auth.login.orContinueWith')}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    type="button"
                    onClick={() => startSsoLogin('google')}
                    disabled={isLoading}
                    className="inline-flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-1 disabled:opacity-70"
                  >
                    <GoogleIcon />
                    {t('auth.login.googleButton')}
                  </button>
                  <button
                    type="button"
                    onClick={() => startSsoLogin('microsoft')}
                    disabled={isLoading}
                    className="inline-flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-1 disabled:opacity-70"
                  >
                    <MicrosoftIcon />
                    {t('auth.login.microsoftButton')}
                  </button>
                </div>

                <div className="text-center text-sm text-gray-600">
                  {t('auth.login.registerHint')}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="font-medium text-theme-primary hover:text-theme-primary/80"
                  >
                    {t('auth.login.registerLink')}
                  </button>
                </div>
              </>
            )}

            {canCreateTenant && (
              <div className="pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setError('')
                    setNewTenantName('')
                    setIsCreateTenantModalOpen(true)
                  }}
                  className="w-full btn btn-secondary"
                  disabled={isLoading}
                >
                  <Plus className="w-4 h-4" />
                  {t('auth.login.createTenantButton')}
                </button>
              </div>
            )}
          </form>

        </div>
      </div>

      {/* Rodapé legal — links discretos */}
      <div className="mt-6 w-full max-w-md mx-auto text-center">
        <p className="text-xs text-gray-400">
          <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">
            {t('auth.login.footer.privacyPolicy')}
          </Link>
          <span className="mx-2 text-gray-300">·</span>
          <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">
            {t('auth.login.footer.termsOfService')}
          </Link>
          <span className="mx-2 text-gray-300">·</span>
          <Link to="/data-deletion" className="hover:text-gray-600 transition-colors">
            {t('auth.login.footer.dataDeletion')}
          </Link>
        </p>
      </div>

      <Modal
        isOpen={isCreateTenantModalOpen}
        onClose={() => {
          if (isCreatingTenant) return
          setIsCreateTenantModalOpen(false)
        }}
        title={t('auth.login.createTenantModal.title')}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="new-tenant-name" className="label">
              {t('auth.login.createTenantModal.nameLabel')}
            </label>
            <input
              id="new-tenant-name"
              type="text"
              className="input w-full"
              value={newTenantName}
              onChange={(e) => {
                setNewTenantName(e.target.value)
                setError('')
              }}
              placeholder={t('auth.login.createTenantModal.namePlaceholder')}
              disabled={isCreatingTenant}
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setIsCreateTenantModalOpen(false)}
              disabled={isCreatingTenant}
            >
              {t('common.buttons.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmCreateTenant}
              disabled={isCreatingTenant}
            >
              {isCreatingTenant ? t('auth.login.createTenantModal.confirmLoading') : t('auth.login.createTenantModal.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
